import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Iconos } from '../componentes/iconos';
import { Boton, Campo, Entrada, ErrorCaja } from '../componentes/ui';
import { almacenToken, ErrorApi, pedir } from '../lib/api';
import { useSesion } from '../lib/sesion';

/**
 * Cambiar la propia contraseña.
 *
 * ## Por qué existe
 *
 * El API sabía hacerlo desde siempre y **nada en la app lo llamaba nunca**. El
 * resultado era una función a medias con una consecuencia concreta: el padrón
 * reparte contraseñas provisionales y marca la cuenta como «debe cambiarla», y
 * después no había dónde. Una provisional que nadie puede cambiar no es
 * provisional, es la contraseña definitiva de esa persona, elegida por otro y
 * dictada por teléfono.
 *
 * ## Las tres casillas
 *
 * **La actual** se pide aunque ya haya sesión iniciada. No es burocracia: una
 * tablet de caseta se queda abierta sobre un mostrador, y sin este campo
 * cualquiera que pase le cambia la contraseña al guardia y lo deja fuera de su
 * propio turno. El API la exige igualmente; aquí solo se recoge.
 *
 * **La nueva, dos veces.** Una contraseña no se ve al teclearla, así que un
 * dedo torcido en una tablet no se nota hasta el siguiente inicio de sesión —y
 * para entonces quien la escribió ya no recuerda qué quiso poner—. Repetirla es
 * la única forma de que ese error salga a la cara en el momento.
 */
export function ContrasenaPagina() {
  const { usuario, marcarPasswordCambiada } = useSesion();
  const navegar = useNavigate();

  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [listo, setListo] = useState(false);
  const [guardando, setGuardando] = useState(false);

  /** Impuesta: llegó aquí porque no puede ir a ninguna otra parte. */
  const obligada = usuario?.debeCambiarPassword === true;

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    setError(null);

    // Se comprueba antes de mandar nada. Que el servidor no puede detectarlo
    // —solo recibe una contraseña, no dos— es lo de menos; lo que importa es
    // que el aviso llegue sin esperar a una ida y vuelta por la red.
    if (nueva !== repetida) {
      setError('Las dos contraseñas nuevas no son iguales.');
      return;
    }

    setGuardando(true);
    try {
      const nuevaSesion = await pedir<{ token: string }>('/auth/password', {
        metodo: 'POST',
        cuerpo: { passwordActual: actual, passwordNueva: nueva },
        // Aquí un 401 dice «la actual no es esa», no «tu sesión caducó». Sin
        // esto, equivocarse tecleando cerraba la sesión en silencio: salía el
        // mensaje correcto y el siguiente intento ya rebotaba al login.
        noCierraSesion: true,
      });

      // **Hay que guardar el token que devuelve.** Cambiar la contraseña cierra
      // las sesiones anteriores —esa es la gracia: echa al teléfono perdido— y
      // la que está abierta aquí es una de ellas. Sin esto, quien acaba de
      // cambiarla se quedaría fuera en la siguiente petición.
      almacenToken.guardar(nuevaSesion.token);
      marcarPasswordCambiada();
      setListo(true);
    } catch (e) {
      setError(
        e instanceof ErrorApi
          ? // El API distingue «la actual no es esa» de «la nueva no vale», y
            // esa diferencia es justo la que necesita quien está atascado.
            (e.errores?.[0]?.mensaje ?? e.message)
          : 'No se pudo cambiar la contraseña.',
      );
    } finally {
      setGuardando(false);
    }
  }

  if (listo) {
    return (
      <Marco>
        <header className="mb-stack-lg">
          <h1 className="text-headline-md text-on-surface">Contraseña cambiada</h1>
          <p className="mt-1 text-body-md text-on-surface-variant">
            Es la que vas a usar a partir de ahora. No hace falta volver a entrar.
          </p>
        </header>
        <Boton
          variante="primario"
          icono={Iconos.correcto}
          className="w-full"
          onClick={() => navegar('/', { replace: true })}
        >
          Continuar
        </Boton>
      </Marco>
    );
  }

  return (
    <Marco>
      <header className="mb-stack-lg">
        <h1 className="text-headline-md text-on-surface">
          {obligada ? 'Pon tu contraseña' : 'Cambiar contraseña'}
        </h1>
        <p className="mt-1 text-body-md text-on-surface-variant">
          {obligada
            ? 'La que tienes te la dio quien administra el condominio. Elige una tuya antes de seguir.'
            : 'Necesitas la actual para cambiarla, aunque ya hayas entrado.'}
        </p>
      </header>

      <form onSubmit={enviar} className="space-y-5">
        <Campo etiqueta="Contraseña actual">
          <Entrada
            id="actual"
            type="password"
            autoComplete="current-password"
            required
            value={actual}
            onChange={(e) => setActual(e.target.value)}
          />
        </Campo>

        <Campo etiqueta="Contraseña nueva" ayuda="Al menos 10 caracteres.">
          <Entrada
            id="nueva"
            type="password"
            autoComplete="new-password"
            minLength={10}
            required
            value={nueva}
            onChange={(e) => setNueva(e.target.value)}
          />
        </Campo>

        <Campo etiqueta="Repite la nueva">
          <Entrada
            id="repetida"
            type="password"
            autoComplete="new-password"
            required
            value={repetida}
            onChange={(e) => setRepetida(e.target.value)}
            // El aviso sale al salir del campo, no en cada tecla: mientras se
            // escribe la segunda siempre va a diferir de la primera, y decirlo
            // en rojo desde la primera letra es ruido.
            onBlur={() =>
              setError(
                repetida && nueva !== repetida
                  ? 'Las dos contraseñas nuevas no son iguales.'
                  : null,
              )
            }
          />
        </Campo>

        {error && <ErrorCaja>{error}</ErrorCaja>}

        <Boton
          type="submit"
          variante="primario"
          disabled={guardando}
          icono={Iconos.correcto}
          className="w-full"
        >
          {guardando ? 'Guardando…' : 'Cambiar contraseña'}
        </Boton>

        {/* Sin salida si es obligada: irse dejaría la cuenta con la contraseña
            que otro eligió, que es justo lo que esta pantalla viene a terminar. */}
        {!obligada && (
          <Boton type="button" variante="texto" className="w-full" onClick={() => navegar(-1)}>
            Cancelar
          </Boton>
        )}
      </form>
    </Marco>
  );
}

/** Centrada y estrecha: se llega desde el panel y desde el teléfono. */
function Marco({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-sm px-4 py-10">
      <div className="rounded-xl border border-outline-variant bg-surface-container-lowest p-8">
        {children}
      </div>
    </div>
  );
}
